import { createReactExtension } from '@backstage/core-plugin-api';
import { ExternalLink } from '@internal/ui-common';
import { homePlugin } from '@backstage/plugin-home';
import { ListPickerSettingsDialog } from '../listPickerSettingsDialog/ListPickerSettingsDialog';
import { NoContentSelected } from '../noContentSelected/NoContentSelected';
import { NoContentType } from '../noContentSelected/types/NoContentType.enum';
import PhoneIcon from '@mui/icons-material/Phone';
import { Typography } from '@material-ui/core';
import { useEffect } from 'react';
import { useStyles } from './onCall.style';

import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { OnCallSchedule, useOnCall } from './hooks/useOnCall';

export const schemaForSettings = {
  title: 'Select the desired on-call team',
  type: 'object',
  properties: {
    defaultOption: {
      type: 'string',
      enum: ['Loading teams...'], // Placeholder until real data loads
    },
  },
};

function NoOnCallPerson() {
  const { cx, classes } = useStyles();
  return (
    <Typography variant="body1" className={cx(classes.nothingSelected)}>
      No on-call person found.
    </Typography>
  );
}

function OnCallPerson({ scheduleName, onCallPerson }: OnCallSchedule) {
  const { cx, classes } = useStyles();
  return (
    <div>
      <Typography variant="body1">{scheduleName}</Typography>
      {onCallPerson ? (
        <Typography variant="body1">
          <ExternalLink
            to={`https://teams.microsoft.com/l/chat/0/0?users=${onCallPerson}`}
          >
            {onCallPerson}
          </ExternalLink>
        </Typography>
      ) : (
        <Typography variant="body1" className={cx(classes.nothingSelected)}>
          No person assigned
        </Typography>
      )}
    </div>
  );
}

function OnCall(team: any) {
  const { cx, classes } = useStyles();
  const { teamNames, currentSchedules, isLoading, error } = useOnCall(
    team.defaultOption,
  );

  useEffect(() => {
    if (teamNames && teamNames.length > 0) {
      schemaForSettings.properties.defaultOption.enum = teamNames;
    }
  }, [teamNames]);

  if (isLoading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <div className={cx(classes.onCallCardWrapper)}>
      <InfoCard
        title={
          team.defaultOption
            ? `On call in team ${team.defaultOption}`
            : 'Who is on-call'
        }
        className={cx(classes.onCallCard)}
      >
        <section>
          <article>
            <div>
              <PhoneIcon />
            </div>
            <section>
              {!team.defaultOption && (
                <NoContentSelected type={NoContentType.Team} />
              )}
              {team.defaultOption && currentSchedules.length === 0 && (
                <NoOnCallPerson />
              )}
              {team.defaultOption &&
                currentSchedules.length > 0 &&
                currentSchedules.map((currentSchedule, index) => (
                  <OnCallPerson
                    key={index}
                    scheduleName={currentSchedule.scheduleName}
                    onCallPerson={currentSchedule.onCallPerson}
                  />
                ))}
            </section>
          </article>
          <ExternalLink to="https://health.eu1.scalar.zf.com/oncall">
            See more
          </ExternalLink>
        </section>
      </InfoCard>
    </div>
  );
}

export { OnCall };

export const OncallWidget = homePlugin.provide(
  createReactExtension({
    name: 'Oncall widget',
    component: {
      lazy: () => import('./OnCall').then(m => m.OnCall),
    },
    data: {
      title: 'Who is on-call',
      description: 'Shows the current on-call team person within a team.',
      'home.widget.config': {
        layout: {
          width: {
            defaultColumns: 4,
            minColumns: 3,
            maxColumns: 12,
          },
          height: {
            defaultRows: 4,
            minRows: 4,
            maxRows: 5,
          },
        },
        settings: {
          schema: schemaForSettings,
          uiSchema: {
            defaultOption: {
              'ui:widget': ListPickerSettingsDialog,
            },
          },
        },
      },
    },
  }),
);
